import { useState, useEffect } from "react";
import api from "@/lib/api";
import { formatCr, formatLakh, MONEY_UNIT_LABEL, parseLakhInput } from "@/lib/utils-ros";
import { toast } from "sonner";
import { Package, Plus, Trash2, ShieldCheck, Zap, Leaf, Cpu, Wrench, CheckCircle2, X, Loader2, ShoppingCart, Landmark } from "lucide-react";

const MODULES = [
  { key: "safety",         label: "Safety",         icon: ShieldCheck, color: "#F59E0B" },
  { key: "energy",         label: "Energy",         icon: Zap,         color: "#84CC16" },
  { key: "sustainability", label: "Sustainability", icon: Leaf,        color: "#047857" },
  { key: "digital",        label: "Digital MRV",    icon: Cpu,         color: "#0B84F3" },
  { key: "operations",     label: "Operations",     icon: Wrench,      color: "#7E22CE" },
  { key: "quality",        label: "Quality",        icon: CheckCircle2, color: "#0891B2" },
  { key: "carbon",         label: "Carbon",         icon: Package,     color: "#3F3F46" },
  { key: "governance",     label: "Governance",     icon: Landmark,    color: "#07539A" },
];
const MOD_MAP = {
  ...Object.fromEntries(MODULES.map(m => [m.key, m])),
  other: { key: "other", label: "Other", icon: Package, color: "#6B7280" },
};

export function LineItemsPanel({ oppId, oppValue, oppStatus, onChanged, readOnly = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [booking, setBooking] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/opportunities/${oppId}`);
      setItems(data.opportunity?.line_items || data.line_items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load modules");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [oppId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(newItems) {
    try {
      const { data } = await api.put(`/opportunities/${oppId}/line-items`, newItems.map(i => ({
        ...(i.id ? { id: i.id } : {}),
        ergoniq_module: i.ergoniq_module,
        label: i.label,
        qty: i.qty,
        price_cr: parseLakhInput(i.price_cr),
        cost_cr: parseLakhInput(i.cost_cr),
        status: i.status || "in_funnel",
      })));
      setItems(data.line_items || []);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save modules");
      throw e;
    }
  }

  async function removeItem(id) {
    if (!window.confirm("Remove this line item?")) return;
    const next = items.filter(i => i.id !== id);
    await save(next);
    toast.success("Line item removed");
  }

  function toggle(id) {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }

  async function bookSelected() {
    const ids = Array.from(selected);
    if (ids.length === 0) return toast.error("Select at least one module to book");
    setBooking(true);
    try {
      const { data } = await api.post(`/opportunities/${oppId}/create-order`, { line_item_ids: ids });
      toast.success(`Order booked · PO ${data.po_number || "—"} · ${ids.length} module(s)`);
      setSelected(new Set());
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBooking(false); }
  }

  const inFunnel = items.filter(i => i.status === "in_funnel");
  const converted = items.filter(i => i.status === "won");
  const cancelled = items.filter(i => ["lost", "cancelled"].includes(i.status));
  const inFunnelValue = inFunnel.reduce((s, i) => s + (i.price_cr || 0), 0);
  const convertedValue = converted.reduce((s, i) => s + (i.price_cr || 0), 0);
  const selectedValue = inFunnel.filter(i => selected.has(i.id)).reduce((s, i) => s + (i.price_cr || 0), 0);
  const oppLocked = ["won", "lost", "cancelled"].includes(oppStatus);

  return (
    <div className="border border-black/10 rounded-md bg-white" data-testid="line-items-panel">
      <div className="px-3 py-2 border-b border-[#063F73] flex items-center gap-2 flex-wrap bg-[#07539A]">
        <Package size={13} className="!text-white" />
        <span className="text-[11px] uppercase tracking-widest !text-white font-semibold">Modules</span>
        <span className="text-[9px] !text-[#B9DDF0]">Partial order engine</span>
        <span className="text-[9px] !text-[#B9DDF0]">({MONEY_UNIT_LABEL})</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden md:flex items-stretch gap-1.5 tabular-nums">
            <div className="min-w-[72px] px-2 py-1 rounded-[10px] bg-[#F5A23A]">
              <div className="text-[8px] uppercase tracking-wider !text-white/80">In funnel · {inFunnel.length}</div>
              <div className="font-mono text-[12px] leading-tight !text-white">{formatCr(inFunnelValue)}</div>
            </div>
            <div className="min-w-[72px] px-2 py-1 rounded-[10px] bg-[#84CC16]">
              <div className="text-[8px] uppercase tracking-wider !text-white/80">Booked · {converted.length}</div>
              <div className="font-mono text-[12px] leading-tight !text-white">{formatCr(convertedValue)}</div>
            </div>
            <div className="min-w-[72px] px-2 py-1 rounded-[10px] bg-white border border-[#BBD8E7]">
              <div className="text-[8px] uppercase tracking-wider text-[#607D8B]">Total · {items.length}</div>
              <div className="font-mono text-[12px] leading-tight text-[#07539A]">{formatCr(oppValue || 0)}</div>
            </div>
          </div>
          {!oppLocked && !readOnly && (
            <button onClick={() => setAddOpen(true)} data-testid="line-item-add"
                    className="text-[11px] px-2.5 py-1 rounded-full bg-[#E7F5FB] !text-[#07539A] hover:bg-white flex items-center gap-1">
              <Plus size={11} /> Add
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-[var(--text-muted)]"><Loader2 size={14} className="animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-[13px] text-[var(--text-muted)]">
          No modules yet. Add Ergoniq modules to convert them independently (e.g. Safety this month, Energy next quarter).
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <div className="min-w-[570px]">
              <div className="grid grid-cols-[32px_minmax(210px,1fr)_68px_96px_108px_32px] gap-3 items-center px-4 py-2 border-b border-[#D7E8F0] text-[9px] uppercase tracking-widest font-semibold text-[#07539A]">
                <div /><div>Product</div><div className="text-center">Qty</div><div className="text-right">Price</div><div className="text-center">Status</div><div />
              </div>
              <div className="divide-y divide-[#E1EBF0]">
                {items.map(i => {
                  const mod = MOD_MAP[i.ergoniq_module] || MOD_MAP.other;
                  const displayLabel = String(i.label || mod.label).trim();
                  const showCategory = displayLabel.localeCompare(mod.label, undefined, { sensitivity: "base" }) !== 0;
                  const Icon = mod.icon;
                  const isSelectable = i.status === "in_funnel" && !oppLocked && !readOnly;
                  const isSel = selected.has(i.id);
                  return (
                    <div key={i.id} className="grid grid-cols-[32px_minmax(210px,1fr)_68px_96px_108px_32px] gap-3 items-center px-4 py-3" data-testid={`line-item-${i.id}`}>
                      <div className="flex justify-center">
                        {isSelectable ? (
                          <button onClick={() => toggle(i.id)} data-testid={`li-toggle-${i.id}`}
                                  className={`w-4 h-4 rounded-sm border shrink-0 flex items-center justify-center ${
                                    isSel ? "bg-[#07539A] border-[#07539A]" : "border-[#AFC5CF] hover:border-[#07539A]"
                                  }`}>
                            {isSel && <CheckCircle2 size={10} className="!text-white" />}
                          </button>
                        ) : <div className="w-4 h-4" />}
                      </div>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0 border"
                             style={{ background: `${mod.color}18`, color: mod.color, borderColor: `${mod.color}45` }}>
                          <Icon size={15} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-[#12324A] truncate">{displayLabel}</div>
                          <div className="text-[9px] mt-0.5 flex items-center gap-1.5 flex-wrap">
                            {showCategory && <span className="uppercase tracking-wider font-semibold" style={{ color: mod.color }}>{mod.label}</span>}
                            {i.po_ref && <span className="font-mono text-[#78909C]">PO {i.po_ref}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="font-mono text-[12px] text-center text-[#526D7A]">{i.qty || 1}</div>
                      <div className="font-mono text-[13px] tabular-nums text-right text-[#12324A]" title={MONEY_UNIT_LABEL}>{formatLakh(i.price_cr)}</div>
                      <div className="flex justify-center"><StatusBadge status={i.status} /></div>
                      <div className="flex justify-center">
                        {i.status === "in_funnel" && !oppLocked && !readOnly && (
                          <button onClick={() => removeItem(i.id)} data-testid={`li-remove-${i.id}`} className="text-[#90A4AE] hover:text-[#B42318] p-1"><Trash2 size={12} /></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {inFunnel.length > 0 && !oppLocked && !readOnly && (
            <div className="p-3 md:p-4 border-t border-black/10 bg-[var(--copper)]/[0.02] flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-[var(--text-muted)]">
                  {selected.size === 0 ? (
                    <>Select the modules to convert. Unselected ones stay <span className="font-medium">in funnel</span>.</>
                  ) : (
                    <><span className="font-medium text-[var(--copper)]">{selected.size} module{selected.size !== 1 ? "s" : ""}</span> selected · <span className="font-mono">{formatCr(selectedValue)}</span> — remaining stays in funnel</>
                  )}
                </div>
              </div>
              <button onClick={bookSelected} disabled={booking || selected.size === 0}
                      data-testid="li-book-selected"
                      className="h-9 px-4 rounded-md bg-[var(--copper)] hover:bg-[var(--copper-strong)] text-white text-[12px] font-medium flex items-center gap-2 disabled:opacity-40">
                {booking ? <Loader2 size={12} className="animate-spin" /> : <ShoppingCart size={12} />}
                Book selected as order
              </button>
            </div>
          )}
          {cancelled.length > 0 && (
            <div className="px-4 py-2 text-[10px] text-[var(--text-dim)] border-t border-black/5">
              {cancelled.length} cancelled/lost module(s)
            </div>
          )}
        </>
      )}

      {addOpen && <AddLineItemModal
        existingKeys={new Set(items.map(i => i.ergoniq_module))}
        onClose={() => setAddOpen(false)}
        onAdd={async (newRows) => {
          await save([...items, ...newRows]);
          toast.success(`${newRows.length} module${newRows.length === 1 ? "" : "s"} added`);
          setAddOpen(false);
        }}
      />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    in_funnel: { label: "In funnel", color: "#C97715", bg: "#FDE8D2" },
    won: { label: "Booked", color: "#15803D", bg: "#E3F3E7" },
    lost: { label: "Lost", color: "var(--danger)", bg: "rgba(220,38,38,0.10)" },
    cancelled: { label: "Cancelled", color: "var(--text-dim)", bg: "rgba(107,114,128,0.10)" },
  }[status] || { label: status, color: "var(--text-dim)", bg: "rgba(0,0,0,0.06)" };
  return (
    <span className="text-[9px] uppercase tracking-wider font-semibold px-3 py-1 rounded-full shrink-0"
          style={{ color: map.color, background: map.bg }}>{map.label}</span>
  );
}

function AddLineItemModal({ onClose, onAdd, existingKeys = new Set() }) {
  const [selected, setSelected] = useState(() => new Set());
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);

  function toggleModule(key) {
    if (existingKeys.has(key)) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        setRows(r => ({
          ...r,
          [key]: r[key] || { label: MOD_MAP[key].label, qty: 1, price_cr: "", cost_cr: 0 },
        }));
      }
      return next;
    });
  }

  function updateRow(key, patch) {
    setRows(r => ({ ...r, [key]: { ...r[key], ...patch } }));
  }

  async function submit() {
    if (selected.size === 0) return toast.error("Select at least one module");
    const newRows = [];
    for (const key of selected) {
      const row = rows[key] || {};
      const price = parseLakhInput(row.price_cr);
      if (price <= 0) {
        return toast.error(`Enter a price (${MONEY_UNIT_LABEL}) for ${MOD_MAP[key].label}`);
      }
      newRows.push({
        ergoniq_module: key,
        label: (row.label || MOD_MAP[key].label).trim(),
        qty: Number(row.qty) || 1,
        price_cr: price,
        cost_cr: parseLakhInput(row.cost_cr),
        status: "in_funnel",
      });
    }
    setSaving(true);
    try {
      await onAdd(newRows);
    } finally { setSaving(false); }
  }

  const selectedList = Array.from(selected);

  return (
    <div className="fixed inset-0 z-50 bg-[#12324A]/45 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="add-line-item-modal">
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3 shrink-0">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#C9DCE6] flex items-center justify-center text-[#07539A]"><Package size={15} /></div>
          <div className="font-display text-[18px] text-[#12324A] flex-1">Add Ergoniq modules</div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A]" aria-label="Close"><X size={15} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-[12px] text-[#607D8B] rounded-[16px] bg-white border border-[#C9DCE6] px-4 py-3">
            Select one or more modules — click each tile to toggle. Set an individual price for every module.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {MODULES.map(m => {
              const Icon = m.icon;
              const active = selected.has(m.key);
              const exists = existingKeys.has(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => toggleModule(m.key)}
                  disabled={exists}
                  data-testid={`li-module-${m.key}`}
                  className={`min-h-[76px] p-3 rounded-[14px] border flex flex-col items-center justify-center gap-1.5 relative transition-all shadow-[0_5px_14px_rgba(7,83,154,0.08)] ${
                    exists ? "opacity-35 cursor-not-allowed" :
                    active ? "ring-2 ring-white ring-offset-2 ring-offset-[#F3FAFD] scale-[1.02]" :
                    "hover:brightness-110 hover:-translate-y-0.5"
                  }`}
                  style={{ backgroundColor: m.color, borderColor: m.color }}
                  title={exists ? "Already on this opportunity" : active ? "Selected — click to deselect" : "Click to select"}
                >
                  {active && <CheckCircle2 size={13} className="absolute top-2 right-2 !text-white" />}
                  <Icon size={17} className="!text-white" />
                  <span className="text-[11px] text-center leading-tight font-medium !text-white">{m.label}</span>
                </button>
              );
            })}
          </div>

          {selectedList.length > 0 && (
            <div className="space-y-3 border-t border-[#C9DCE6] pt-4">
              <div className="text-[10px] uppercase tracking-widest text-[#07539A] font-semibold">
                {selectedList.length} module{selectedList.length === 1 ? "" : "s"} selected
              </div>
              {selectedList.map(key => {
                const m = MOD_MAP[key];
                const row = rows[key] || {};
                return (
                  <div key={key} className="border border-[#C9DCE6] bg-white rounded-[16px] p-4 space-y-3" data-testid={`li-row-${key}`}>
                    <div className="text-[12px] font-medium" style={{ color: m.color }}>{m.label}</div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Module name</label>
                      <input
                        placeholder="e.g. 3 plants pilot"
                        value={row.label || ""}
                        onChange={e => updateRow(key, { label: e.target.value })}
                        data-testid={`li-label-${key}`}
                        className="w-full mt-1 h-10 px-3 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px]"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Qty</label>
                        <input type="number" min="1" value={row.qty ?? 1}
                               onChange={e => updateRow(key, { qty: e.target.value })}
                               data-testid={`li-qty-${key}`}
                               className="w-full mt-1 h-10 px-3 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px] tabular-nums" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Price ({MONEY_UNIT_LABEL})</label>
                        <input type="number" step="0.01" min="0" value={row.price_cr ?? ""}
                               onChange={e => updateRow(key, { price_cr: e.target.value })}
                               data-testid={`li-price-${key}`}
                               className="w-full mt-1 h-10 px-3 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px] tabular-nums" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Cost ({MONEY_UNIT_LABEL})</label>
                        <input type="number" step="0.01" min="0" value={row.cost_cr ?? 0}
                               onChange={e => updateRow(key, { cost_cr: e.target.value })}
                               data-testid={`li-cost-${key}`}
                               className="w-full mt-1 h-10 px-3 rounded-[11px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px] tabular-nums" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
        <div className="p-5 pt-4 border-t border-[#C9DCE6] bg-[#F3FAFD] shrink-0">
          <button onClick={submit} disabled={saving || selected.size === 0} data-testid="li-save"
                  className="w-full h-11 rounded-[14px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[13px] font-medium flex items-center justify-center gap-2 disabled:opacity-40 shadow-[0_7px_18px_rgba(7,83,154,0.16)]">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            {selected.size === 0 ? "Select modules above" : `Add ${selected.size} module${selected.size === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
